import {
  Box,
  Card,
  Group,
  Loader,
  Paper,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Star,
} from "tabler-icons-react";
import { useGetClassesQuery } from "../../../redux/features/classeApi";
import { useLazyGetAllParentsActivitiesStatsQuery } from "../../../redux/features/parentApi";

// ─── Visual config (purely cosmetic, no business logic) ───────────────────────

const STEP_COLORS = {
  STEP_1: { border: "#dee2e6", bg: "#f8f9fa", accent: "#868e96" },
  STEP_2: { border: "#fcc419", bg: "#fff9db", accent: "#f59e0b" },
  STEP_3: { border: "#dee2e6", bg: "#f8f9fa", accent: "#868e96" },
  STEP_4: { border: "#dee2e6", bg: "#f8f9fa", accent: "#868e96" },
  STEP_5: { border: "#51cf66", bg: "#ebfbee", accent: "#2b8a3e" },
};

const FEATURE_META = {
  DIAGNOSTIC: { color: "#10b981" },
  LESSON_QUIZ: { color: "#3b82f6" },
  EXERCISE_CORRECTION: { color: "#1e40af" },
};

const FEATURE_NAME_MAP = {
  DIAGNOSTIC: "Diagnostic",
  LESSON_QUIZ: "Vérifier la leçon",
  EXERCISE_CORRECTION: "Corriger l'exercice",
};

// ─── Helper: aggregate parents stats across multiple class responses ──────────

function aggregateParentsStats(parentsLists) {
  const mergedMap = new Map();

  parentsLists.forEach((list) => {
    if (!list) return;
    list.forEach((parent) => {
      const pid = parent.parent_id;
      if (!mergedMap.has(pid)) {
        // Clone object to avoid mutating redux state
        mergedMap.set(pid, {
          parent_id: pid,
          parent_name: parent.parent_name,
          parent_phone: parent.parent_phone,
          category: parent.category,
          total_activities: parent.total_activities || 0,
          total_credits_spent: parent.total_credits_spent || 0,
          activities_by_type: {
            DIAGNOSTIC: { count: parent.activities_by_type?.DIAGNOSTIC?.count || 0 },
            LESSON_QUIZ: { count: parent.activities_by_type?.LESSON_QUIZ?.count || 0 },
            EXERCISE_CORRECTION: { count: parent.activities_by_type?.EXERCISE_CORRECTION?.count || 0 },
          }
        });
      } else {
        const existing = mergedMap.get(pid);
        existing.total_activities += (parent.total_activities || 0);
        existing.total_credits_spent += (parent.total_credits_spent || 0);
        
        if (parent.activities_by_type) {
          Object.keys(parent.activities_by_type).forEach((type) => {
            if (!existing.activities_by_type[type]) {
              existing.activities_by_type[type] = { count: 0 };
            }
            existing.activities_by_type[type].count += (parent.activities_by_type[type].count || 0);
          });
        }

        // Keep the most advanced category priority
        const categoryPriority = {
          "USER": 1,
          "USER_ACTIVE": 2,
          "CLIENT": 3,
          "CLIENT_ENGAGE": 4,
          "CLIENT_ACTIF": 5
        };
        const p1 = categoryPriority[existing.category] || 0;
        const p2 = categoryPriority[parent.category] || 0;
        if (p2 > p1) {
          existing.category = parent.category;
        }
      }
    });
  });

  return Array.from(mergedMap.values());
}

const isClientStage = (category) => {
  return ["CLIENT", "CLIENT_ENGAGE", "CLIENT_ACTIF", "CLIENT_IMPACTE"].includes(
    category,
  );
};

// ─── Per-class parents fetcher (side-effect component, renders nothing) ────────

function ClassParentsFetcher({
  classeId,
  startDate,
  endDate,
  onLoaded,
}) {
  const [trigger] = useLazyGetAllParentsActivitiesStatsQuery();

  useEffect(() => {
    let active = true;

    const loadAll = async () => {
      try {
        const res1 = await trigger({
          start_date: startDate,
          end_date: endDate,
          classe_id: classeId,
          limit: 100,
          offset: 0,
        }).unwrap();

        if (!active) return;
        const count = res1?.data?.count || 0;
        let results = res1?.data?.results || [];

        if (count > 100) {
          const promises = [];
          for (let offset = 100; offset < count; offset += 100) {
            promises.push(
              trigger({
                start_date: startDate,
                end_date: endDate,
                classe_id: classeId,
                limit: 100,
                offset,
              }).unwrap()
            );
          }
          const responses = await Promise.all(promises);
          if (!active) return;
          responses.forEach((res) => {
            if (res?.data?.results) {
              results = [...results, ...res.data.results];
            }
          });
        }
        
        if (active) {
          onLoaded(results);
        }
      } catch (err) {
        console.error("Error fetching class parents stats:", err);
      }
    };

    loadAll();

    return () => {
      active = false;
    };
  }, [classeId, startDate, endDate, trigger, onLoaded]);

  return null;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AhaPathStats({
  dateRange,
  selectedGrades,
}) {
  const [globalClassDataMap, setGlobalClassDataMap] = useState({});
  const [globalParentsList, setGlobalParentsList] = useState(null);
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);

  const [trigger] = useLazyGetAllParentsActivitiesStatsQuery();

  const startDate = dateRange.start.format("YYYY-MM-DD");
  const endDate = dateRange.end.format("YYYY-MM-DD");

  // Reset cache when filters change
  useEffect(() => {
    setGlobalClassDataMap({});
    setGlobalParentsList(null);
  }, [selectedGrades, startDate, endDate]);

  // ── Classes ──────────────────────────────────────────────────────────────────
  const { data: classesData } = useGetClassesQuery();

  const activeClassIds = useMemo(() => {
    if (selectedGrades.size === 0) return [];
    return (classesData?.data || [])
      .filter((cls) =>
        Array.from(selectedGrades).some((grade) =>
          cls.name.toUpperCase().includes(grade.toUpperCase()),
        ),
      )
      .map((cls) => cls.id);
  }, [selectedGrades, classesData]);

  const isAll = activeClassIds.length === 0;

  // ── Global query (when "Toutes") ─────────────────────────────────────────────
  useEffect(() => {
    if (!isAll) {
      setGlobalParentsList(null);
      return;
    }
    
    let active = true;
    setIsGlobalLoading(true);

    const loadGlobal = async () => {
      try {
        const res1 = await trigger({
          start_date: startDate,
          end_date: endDate,
          limit: 100,
          offset: 0,
        }).unwrap();

        if (!active) return;
        const count = res1?.data?.count || 0;
        let results = res1?.data?.results || [];

        if (count > 100) {
          const promises = [];
          for (let offset = 100; offset < count; offset += 100) {
            promises.push(
              trigger({
                start_date: startDate,
                end_date: endDate,
                limit: 100,
                offset,
              }).unwrap()
            );
          }
          const responses = await Promise.all(promises);
          if (!active) return;
          responses.forEach((res) => {
            if (res?.data?.results) {
              results = [...results, ...res.data.results];
            }
          });
        }

        if (active) {
          setGlobalParentsList(results);
        }
      } catch (err) {
        console.error("Error loading global parents stats:", err);
      } finally {
        if (active) {
          setIsGlobalLoading(false);
        }
      }
    };

    loadGlobal();

    return () => {
      active = false;
    };
  }, [isAll, startDate, endDate, trigger]);

  // ── Aggregate stats ───────────────────────────────────────────────────────────
  const allParents = useMemo(() => {
    if (isAll) {
      return globalParentsList || [];
    }
    const lists = activeClassIds.map((id) => globalClassDataMap[id]);
    return aggregateParentsStats(lists);
  }, [isAll, globalParentsList, globalClassDataMap, activeClassIds]);

  const isLoading = isAll
    ? isGlobalLoading || globalParentsList === null
    : activeClassIds.some((id) => !globalClassDataMap[id]);

  // ── Funnel steps ─────────────────────────────────────────────────────────────
  //
  //  Step 1 · Baseline  : tous les parents actifs (qui ont fait au moins 1 activité)
  //  Step 2 · Candidate : parents ayant fait au moins 1 correction (EXERCISE_CORRECTION)
  //  Step 3 · Retenu    : parents ayant utilisé au moins 2 features différentes
  //  Step 4 · Client    : parents CLIENT+ selon parents/all-activities-stats
  //  Step 5 · Actif PMF : parents devenus clients actifs (CLIENT_ACTIF)

  const stats = useMemo(() => {
    const step1Count = allParents.length;

    const step2Count = allParents.filter((p) => {
      return (p.activities_by_type?.EXERCISE_CORRECTION?.count || 0) > 0;
    }).length;

    const step3Count = allParents.filter((p) => {
      let distinctFeatures = 0;
      if ((p.activities_by_type?.DIAGNOSTIC?.count || 0) > 0) distinctFeatures++;
      if ((p.activities_by_type?.LESSON_QUIZ?.count || 0) > 0) distinctFeatures++;
      if ((p.activities_by_type?.EXERCISE_CORRECTION?.count || 0) > 0) distinctFeatures++;
      return distinctFeatures >= 2;
    }).length;

    const step4Count = allParents.filter((p) => isClientStage(p.category)).length;

    const step5Count = allParents.filter((p) => p.category === "CLIENT_ACTIF").length;

    const pct = (n) => (step1Count > 0 ? Math.round((n / step1Count) * 100) : 0);

    return {
      step1: { count: step1Count, pct: 100 },
      step2: { count: step2Count, pct: pct(step2Count) },
      step3: { count: step3Count, pct: pct(step3Count) },
      step4: { count: step4Count, pct: pct(step4Count) },
      step5: { count: step5Count, pct: pct(step5Count) },
    };
  }, [allParents]);

  // ── AHA DETECTOR — lift calculation ──────────────────────────────────────────
  //
  //  Calculs 100% exacts basés sur la liste des parents :
  //    nYes     = nb de parents ayant fait cette feature (au moins 1 fois)
  //    nNo      = total cohort parents − nYes
  //    nConvYes = nb de parents clients ayant fait cette feature
  //    nConvNo  = nb de parents clients n'ayant PAS fait cette feature
  //
  //    convYes  = (nConvYes / nYes) * 100
  //    convNo   = (nConvNo / nNo) * 100
  //    lift     = convYes − convNo

  const detectorRows = useMemo(() => {
    const totalParentsCount = allParents.length;
    if (totalParentsCount === 0) return [];

    const featureKeys = ["DIAGNOSTIC", "LESSON_QUIZ", "EXERCISE_CORRECTION"];

    return featureKeys
      .map((key) => {
        const parentsWhoUsed = allParents.filter((p) => {
          return (p.activities_by_type?.[key]?.count || 0) > 0;
        });
        const parentsWhoDidNotUse = allParents.filter((p) => {
          return (p.activities_by_type?.[key]?.count || 0) === 0;
        });

        const nYes = parentsWhoUsed.length;
        const nNo = parentsWhoDidNotUse.length;

        const nConvYes = parentsWhoUsed.filter((p) =>
          isClientStage(p.category),
        ).length;
        const nConvNo = parentsWhoDidNotUse.filter((p) =>
          isClientStage(p.category),
        ).length;

        const cohortPct = Math.round((nYes / totalParentsCount) * 100);
        const convYes = nYes > 0 ? Math.round((nConvYes / nYes) * 100) : 0;
        const convNo = nNo > 0 ? Math.round((nConvNo / nNo) * 100) : 0;
        const lift = convYes - convNo;

        return {
          key,
          name: FEATURE_NAME_MAP[key] || key,
          cohortPct,
          convYes,
          convNo,
          lift,
          nYes,
          nNo,
          nConvYes,
          nConvNo,
        };
      })
      .sort((a, b) => b.lift - a.lift); // Winner = highest lift
  }, [allParents]);

  // Winner = row with highest lift (computed, not hardcoded)
  const winnerKey = detectorRows.length > 0 ? detectorRows[0].key : null;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Box mt="xl">
      {/* Per-class fetchers */}
      {!isAll &&
        activeClassIds.map((id) => (
          <ClassParentsFetcher
            key={`parents-${id}-${startDate}-${endDate}`}
            classeId={id}
            startDate={startDate}
            endDate={endDate}
            onLoaded={(results) =>
              setGlobalClassDataMap((p) => ({ ...p, [id]: results }))
            }
          />
        ))}

      {/* Header */}
      <Box mb="md">
        <Group spacing="sm" align="center">
          <ThemeIcon variant="light" color="blue" size={32} radius="md">
            <Activity size={20} />
          </ThemeIcon>
          <Title
            order={3}
            sx={{ fontSize: "20px", fontWeight: 700, color: "#1c7ed6" }}
          >
            Vue 3 — Chemin critique vers l'habitude (Aha Path)
          </Title>
        </Group>
        <Text size="sm" color="dimmed" mt={4}>
          Le parcours type d'un parent qui passe de User à Client actif
          (habitude installée). % = taux de passage cumulatif depuis la baseline
          · cohorte filtrée selon Classe.
        </Text>
      </Box>

      {/* ── Funnel (5 étapes mesurées) ── */}
      <Paper
        p="xl"
        radius="lg"
        shadow="sm"
        withBorder
        mb="xl"
        sx={{ backgroundColor: "#ffffff" }}
      >
        {isLoading ? (
          <Group position="center" py="xl">
            <Loader size="md" />
            <Text size="sm" color="dimmed">
              Chargement des indicateurs...
            </Text>
          </Group>
        ) : (
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "nowrap",
              gap: "10px",
              [`@media (max-width: ${theme.breakpoints.md}px)`]: {
                flexDirection: "column",
                gap: "20px",
              },
            })}
          >
            {/* STEP 1 */}
            <FunnelCard
              label="Étape 1"
              title={
                <>
                  1<sup>re</sup> activité
                  <br />
                  (toute feature)
                </>
              }
              pct={stats.step1.pct}
              sub={`Baseline · ${stats.step1.count} parents`}
              colors={STEP_COLORS.STEP_1}
            />
            <Arrow />

            {/* STEP 2 */}
            <FunnelCard
              label="Aha Candidate"
              title={
                <>
                  1<sup>re</sup> correction
                  <br />
                  réussie
                </>
              }
              pct={stats.step2.pct}
              sub={`${stats.step2.count} parents`}
              colors={STEP_COLORS.STEP_2}
              accentLabel
            />
            <Arrow />

            {/* STEP 3 */}
            <FunnelCard
              label="Étape 3"
              title={
                <>
                  2<sup>e</sup> feature
                  <br />
                  utilisée
                </>
              }
              pct={stats.step3.pct}
              sub={`${stats.step3.count} parents`}
              colors={STEP_COLORS.STEP_3}
            />
            <Arrow />

            {/* STEP 4 */}
            <FunnelCard
              label="Étape 4"
              title="Paiement (Client)"
              pct={stats.step4.pct}
              sub={`${stats.step4.count} parents`}
              colors={STEP_COLORS.STEP_4}
            />
            <Arrow />

            {/* STEP 5 */}
            <FunnelCard
              label="CIBLE PMF"
              title="Client actif (8+ sessions)"
              pct={stats.step5.pct}
              sub={`${stats.step5.count} parents · obj. 40%`}
              colors={STEP_COLORS.STEP_5}
              accentLabel
            />
          </Box>
        )}
      </Paper>

      {/* ── AHA DETECTOR ── */}
      <Box mb="md" mt="xl">
        <Title
          order={3}
          sx={{ fontSize: "18px", fontWeight: 700, color: "#2c3e50" }}
        >
          AHA DETECTOR — QUELLE FEATURE A LE PLUS D'EFFET ?
        </Title>
        <Text size="sm" color="dimmed" mt={4}>
          Pour chaque feature, le <strong>lift</strong> = taux de conversion
          vers Client des parents ayant fait la feature vs les parents ne l'ayant pas faite sur la même période. La feature avec le lift le plus élevé
          est le aha gagnant.{" "}
          <Text component="span" size="xs" color="blue" weight={600}>
            ✓ Unité : parents uniques (cohorte homogène) — calculs mathématiques exacts.
          </Text>
        </Text>
      </Box>

      <Paper
        radius="lg"
        shadow="sm"
        withBorder
        sx={{ overflow: "hidden", backgroundColor: "#ffffff" }}
      >
        {isLoading ? (
          <Group position="center" py="xl">
            <Loader size="md" />
            <Text size="sm" color="dimmed">
              Chargement des données...
            </Text>
          </Group>
        ) : detectorRows.length === 0 ? (
          <Box p="xl" sx={{ textAlign: "center" }}>
            <Text size="sm" color="dimmed">
              Aucune donnée d'activité trouvée pour la période sélectionnée.
            </Text>
          </Box>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table
              verticalSpacing="md"
              horizontalSpacing="xl"
              sx={{ minWidth: 600 }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f8f9fa" }}>
                  <th style={thStyle}>FEATURE</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>
                    % PARENTS COHORTE
                  </th>
                  <th style={{ ...thStyle, textAlign: "right" }}>
                    CONV. → CLIENT SI CETTE FEATURE
                  </th>
                  <th style={{ ...thStyle, textAlign: "right" }}>
                    CONV. → CLIENT SI SANS CETTE FEATURE
                  </th>
                  <th style={{ ...thStyle, textAlign: "right" }}>LIFT</th>
                </tr>
              </thead>
              <tbody>
                {detectorRows.map((row) => {
                  const meta = FEATURE_META[row.key] || { color: "#868e96" };
                  const isWinner = row.key === winnerKey && row.lift > 0;
                  const isPos = row.lift > 0;
                  const isNeg = row.lift < 0;

                  let liftColor = "#495057";
                  let liftWeight = 600;
                  if (row.lift >= 10) {
                    liftColor = "#2b8a3e";
                    liftWeight = 800;
                  } else if (isPos) {
                    liftColor = "#40c057";
                    liftWeight = 700;
                  } else if (isNeg) {
                    liftColor = "#fa5252";
                    liftWeight = 700;
                  }

                  return (
                    <tr
                      key={row.key}
                      style={{
                        borderBottom: "1px solid #dee2e6",
                        backgroundColor: isWinner
                          ? "rgba(235, 251, 238, 0.4)"
                          : "transparent",
                      }}
                    >
                      {/* Feature */}
                      <td>
                        <Group spacing="xs">
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: meta.color,
                              display: "inline-block",
                            }}
                          />
                          <Text
                            size="sm"
                            weight={700}
                            color="dark"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            {row.name}
                            {isWinner && (
                              <Star
                                size={14}
                                color="#f59e0b"
                                fill="#f59e0b"
                                style={{
                                  display: "inline-block",
                                  verticalAlign: "middle",
                                }}
                              />
                            )}
                          </Text>
                        </Group>
                      </td>

                      {/* % cohorte */}
                      <td style={{ textAlign: "right" }}>
                        <Text size="sm" weight={600} color="dark">
                          {row.cohortPct}%
                        </Text>
                        <Text
                          size="10px"
                          color="dimmed"
                          weight={500}
                          style={{ marginTop: 2 }}
                        >
                          {row.nYes} parents
                        </Text>
                      </td>

                      {/* Conv si oui */}
                      <td style={{ textAlign: "right" }}>
                        <Text size="sm" weight={600} color="dark">
                          {row.convYes}%
                        </Text>
                        <Text
                          size="10px"
                          color="dimmed"
                          weight={500}
                          style={{ marginTop: 2 }}
                        >
                          {row.nConvYes} clients
                        </Text>
                      </td>

                      {/* Conv si non */}
                      <td style={{ textAlign: "right" }}>
                        <Text size="sm" weight={600} color="dark">
                          {row.convNo}%
                        </Text>
                        <Text
                          size="10px"
                          color="dimmed"
                          weight={500}
                          style={{ marginTop: 2 }}
                        >
                          {row.nConvNo} clients
                        </Text>
                      </td>

                      {/* Lift */}
                      <td style={{ textAlign: "right" }}>
                        <Group
                          spacing={4}
                          position="right"
                          align="center"
                          style={{ display: "inline-flex" }}
                        >
                          {isPos && (
                            <ChevronUp
                              size={14}
                              color={liftColor}
                              style={{ strokeWidth: 3 }}
                            />
                          )}
                          {isNeg && (
                            <ChevronDown
                              size={14}
                              color={liftColor}
                              style={{ strokeWidth: 3 }}
                            />
                          )}
                          <Text size="sm" weight={liftWeight} color={liftColor}>
                            {isPos ? "+" : ""}
                            {row.lift} {Math.abs(row.lift) === 1 ? "pt" : "pts"}
                          </Text>
                        </Group>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Paper>
    </Box>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function FunnelCard({ label, title, pct, sub, colors, accentLabel }) {
  return (
    <Card
      p="md"
      radius="md"
      withBorder
      sx={{
        flex: 1,
        width: "100%",
        minHeight: "135px",
        border: `2px solid ${colors.border}`,
        backgroundColor: colors.bg,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        boxShadow: accentLabel
          ? `0 2px 8px ${colors.border}33`
          : "0 1px 3px rgba(0,0,0,0.02)",
      }}
    >
      <Text
        size="10px"
        color={accentLabel ? colors.accent : "dimmed"}
        weight={accentLabel ? 800 : 700}
        transform="uppercase"
        sx={{ letterSpacing: 0.5 }}
      >
        {label}
      </Text>
      <Text
        size="xs"
        weight={accentLabel ? 800 : 700}
        color="dark"
        mt={4}
        sx={{
          minHeight: "36px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {title}
      </Text>
      <Text
        size="26px"
        weight={800}
        color="dark"
        my={4}
        sx={{ lineHeight: 1.1 }}
      >
        {pct}%
      </Text>
      <Text size="10px" color="dimmed" weight={600}>
        {sub}
      </Text>
    </Card>
  );
}

function Arrow() {
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#adb5bd",
        flexShrink: 0,
        [`@media (max-width: ${theme.breakpoints.md}px)`]: {
          transform: "rotate(90deg)",
        },
      })}
    >
      <ArrowRight size={20} />
    </Box>
  );
}

const thStyle = {
  color: "#495057",
  fontWeight: 700,
  fontSize: "11px",
  letterSpacing: 0.5,
};
